import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { CHECKLIST_LABELS, fmtDate } from "../lib/constants";
import { PriorityBadge, StatusBadge, Avatar, ProgressBar } from "../components/Badges";
import { Checkbox } from "../components/ui/checkbox";
import { Rocket, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function Deploys() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => api.get("/deploys").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (dev, key) => {
    if (!canWrite) return;
    const newChecklist = { ...dev.checklist, [key]: !dev.checklist[key] };
    setItems((prev) => prev.map((d) => (d.id === dev.id ? { ...d, checklist: newChecklist } : d)));
    try {
      await api.put(`/developments/${dev.id}`, { checklist: newChecklist });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const markReady = async (dev) => {
    try {
      await api.put(`/developments/${dev.id}`, { status: "listo_deploy" });
      toast.success(`${dev.dev_id} marcado como Listo para Deploy`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Liberación</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2"><Rocket className="h-7 w-7" />Próximos Deploys</h1>
      </div>

      <div className="p-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {items.map((d) => (
          <div key={d.id} data-testid={`deploy-card-${d.dev_id}`} className="border border-border bg-white rounded-sm">
            <div className="border-b border-border px-5 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400">{d.dev_id}</span>
                  <PriorityBadge priority={d.priority} />
                </div>
                <h3 className="font-heading font-bold text-lg mt-1 truncate">{d.title}</h3>
              </div>
              <StatusBadge status={d.status} />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Checklist de liberación</span>
                <span className="font-mono text-xs">{d.checklist_done}/{d.checklist_total}</span>
              </div>
              <ProgressBar value={(d.checklist_done / d.checklist_total) * 100} color={d.ready ? "#16A34A" : "#0A0A0A"} height={4} />
              <div className="mt-4 space-y-2">
                {Object.keys(CHECKLIST_LABELS).map((k) => (
                  <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer group">
                    <Checkbox data-testid={`deploy-${d.dev_id}-${k}`} checked={!!d.checklist[k]} onCheckedChange={() => toggle(d, k)} disabled={!canWrite} />
                    <span className={d.checklist[k] ? "text-slate-900" : "text-slate-500"}>{CHECKLIST_LABELS[k]}</span>
                  </label>
                ))}
              </div>
              {canWrite && d.status !== "listo_deploy" && d.status !== "en_produccion" && (
                <button
                  data-testid={`ready-deploy-${d.dev_id}`}
                  onClick={() => markReady(d)}
                  disabled={!d.ready}
                  className={`mt-5 w-full h-10 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 transition-colors duration-200 ${
                    d.ready ? "bg-teal-600 hover:bg-teal-700 text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {d.ready ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                  {d.ready ? "Marcar Listo para Deploy" : "Completa el checklist para habilitar"}
                </button>
              )}
              {d.status === "listo_deploy" && (
                <div className="mt-5 w-full h-10 rounded-sm bg-teal-50 border border-teal-200 text-teal-700 font-semibold text-sm flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Listo para Deploy
                </div>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400">No hay desarrollos en fase de liberación.</p>}
      </div>
    </div>
  );
}
